// Content for the 13 near-identical service/industry/solution detail pages —
// each renders through <DetailPage data={...} />.

const demoBooking = {
  ctaButton: "Book a Demo",
};

export const detailPages = {
  "bespoke-applications": {
    title: "Bespoke Applications (Mobile/Web) | KlarityIQ",
    category: "Services",
    heading: "Bespoke Applications (Mobile/Web)",
    lede: "Custom mobile and web applications built around how your operation actually works — not retrofitted from an off-the-shelf template.",
    features: [
      { icon: "▤", title: "Purpose-Built Interfaces", body: "Mobile and web apps designed around your floor, warehouse, or field workflows, not generic dashboards." },
      { icon: "↻", title: "Rapid Iteration", body: "Ship a working pilot in weeks, then refine based on how your team actually uses it." },
      { icon: "◫", title: "Native + Web Coverage", body: "iOS, Android, and responsive web from a single shared backend and data model." },
      { icon: "⇋", title: "Connected to the Platform", body: "Every app plugs directly into your IoT data and agentic AI layer — no separate integration project." },
    ],
    ctaHeading: "Have a specific app in mind?",
    ctaBody: "Tell us about the workflow and we'll scope a pilot.",
    ...demoBooking,
  },
  "custom-agents-automation": {
    title: "Custom Agents and Automation | KlarityIQ",
    category: "Services",
    heading: "Custom Agents and Automation",
    lede: "Agents built around your specific decisions and workflows — not a generic automation template.",
    features: [
      { icon: "◔", title: "Workflow-Specific Agents", body: "Built around your SOPs and edge cases, not a one-size-fits-all rule engine." },
      { icon: "⇋", title: "Multi-Step Automation", body: "Agents that chain several decisions together end-to-end, not single-trigger alerts." },
      { icon: "⛭", title: "Configurable Guardrails", body: "You define exactly what's autonomous and what needs sign-off." },
      { icon: "↝", title: "Iteration Built In", body: "Agents get refined as your team reviews real decisions, not set once and forgotten." },
    ],
    ctaHeading: "See a custom agent in action",
    ctaBody: "We'll walk through a scenario close to your own workflow.",
    ...demoBooking,
  },
  "native-ai-ml": {
    title: "Native AI and ML | KlarityIQ",
    category: "Services",
    heading: "Native AI and ML",
    lede: "Built-in models for anomaly detection, forecasting, and natural-language querying — no separate ML stack to stand up and maintain.",
    features: [
      { icon: "◑", title: "Anomaly Detection", body: "Flag abnormal equipment or process behavior before it becomes downtime." },
      { icon: "↗", title: "Forecasting", body: "Predict demand, throughput, and maintenance needs from historical and live data." },
      { icon: "⌗", title: "Natural-Language Querying", body: "Ask questions about your operational data in plain English." },
      { icon: "✓", title: "No Separate ML Team Required", body: "Pre-trained models tuned to your data, not a from-scratch data science project." },
    ],
    ctaHeading: "Curious what the models can see in your data?",
    ctaBody: "Bring a sample dataset and we'll show you.",
    ...demoBooking,
  },
  "edge-open-connectivity": {
    title: "Edge and Open Connectivity | KlarityIQ",
    category: "Services",
    heading: "Edge and Open Connectivity",
    lede: "Sensor and device integration, real-time data pipelines, and an open ecosystem that connects to what you already run.",
    features: [
      { icon: "⚡", title: "Pre-Built Connectors", body: "PLCs, sensors, meters, and legacy equipment, without custom middleware for most deployments." },
      { icon: "▣", title: "Edge Processing", body: "Local processing for sites with limited or unreliable connectivity, with sync-on-reconnect." },
      { icon: "⇄", title: "Open Ecosystem", body: "Works alongside SAP, Oracle NetSuite, AWS, Azure, and standard industrial protocols like OPC-UA and MQTT." },
      { icon: "↻", title: "Real-Time Pipelines", body: "Sub-second latency from edge to platform." },
    ],
    ctaHeading: "Ready to connect your operation?",
    ctaBody: "Talk to us about the systems and sensors you already have.",
    ...demoBooking,
  },
  "scale-governance-insights": {
    title: "Scale, Governance and Insights | KlarityIQ",
    category: "Services",
    heading: "Scale, Governance and Insights",
    lede: "Role-based permissions, version tracking, and a live view of what's running, who's running it, and whether it's compliant — across every site.",
    features: [
      { icon: "⛭", title: "Role-Based Permissions", body: "Control who can see, edit, or deploy agents and automations, enforced across every team and site." },
      { icon: "⌗", title: "Version Tracking", body: "Full history of changes to every agent and workflow." },
      { icon: "◈", title: "Global Insights", body: "A live view of what's running across every site from one console." },
      { icon: "↗", title: "Built to Scale", body: "The same platform for a single line or a global rollout." },
    ],
    ctaHeading: "Rolling out across multiple sites?",
    ctaBody: "Let's talk about governance from day one, not as an afterthought.",
    ...demoBooking,
  },
  "security-compliance": {
    title: "Security and Compliance | KlarityIQ",
    category: "Services",
    heading: "Security and Compliance",
    lede: "Encrypted data, full audit trails, and support for the compliance frameworks regulated industries require.",
    features: [
      { icon: "⛨", title: "Encryption Everywhere", body: "Data encrypted in transit and at rest." },
      { icon: "⌗", title: "Full Audit Trails", body: "Every automated decision logged with the reasoning behind it." },
      { icon: "✓", title: "Compliance-Ready", body: "Built to support GxP, audit-readiness, and industry-specific regulatory requirements." },
      { icon: "◑", title: "Access Controls", body: "Fine-grained, role-based access down to the action type." },
    ],
    ctaHeading: "Working in a regulated environment?",
    ctaBody: "Let's talk through your specific compliance requirements.",
    ...demoBooking,
  },
  "logistics-warehouse": {
    title: "Logistics & Warehouse | KlarityIQ",
    category: "Industry",
    heading: "Logistics & Warehouse",
    lede: "Real-time throughput visibility and agents that reroute freight and rebalance capacity without a dispatcher on every call.",
    features: [
      { icon: "◈", title: "Real-Time Throughput Visibility", body: "Track flow across facilities from one live view." },
      { icon: "⇄", title: "Automated Rerouting", body: "Agents shift load and capacity automatically during demand spikes." },
      { icon: "↝", title: "Exception-Based Alerts", body: "Stop manually tracking everything — get flagged only what needs attention." },
      { icon: "⚡", title: "Warehouse Floor Integration", body: "Connects to WMS, scanners, and conveyor/sortation systems." },
    ],
    ctaHeading: "Running a multi-facility operation?",
    ctaBody: "Let's talk about where rerouting and visibility could help first.",
    ...demoBooking,
  },
  "discrete-manufacturing": {
    title: "Discrete Manufacturing | KlarityIQ",
    category: "Industry",
    heading: "Discrete Manufacturing",
    lede: "Connect PLCs and line sensors so agents can catch defects and equipment issues before they cascade into downtime.",
    features: [
      { icon: "◎", title: "Predictive Maintenance", body: "Catch degrading equipment before it fails." },
      { icon: "✓", title: "Automated Defect Flagging", body: "Quality issues caught and routed on the line, not after the fact." },
      { icon: "⌗", title: "Work Order Automation", body: "Sensor thresholds trigger electronic work orders automatically." },
      { icon: "⇋", title: "MES/ERP Integration", body: "Connects to the systems already running your production floor." },
    ],
    ctaHeading: "Want to see it on your line?",
    ctaBody: "We'll walk through a scenario close to your process.",
    ...demoBooking,
  },
  pharmaceutical: {
    title: "Pharmaceutical | KlarityIQ",
    category: "Industry",
    heading: "Pharmaceutical",
    lede: "Connected equipment and governed autonomy — built for environments where every automated action needs an audit trail.",
    features: [
      { icon: "⚡", title: "Device Fleet Monitoring", body: "Preventive servicing across every connected instrument." },
      { icon: "◑", title: "Strict Guardrails", body: "Human-in-the-loop review for every regulated action." },
      { icon: "⌗", title: "Full Audit Trail", body: "Every automated decision recorded to support compliance reviews." },
      { icon: "⛨", title: "GxP-Ready Foundations", body: "Built with the compliance requirements of regulated manufacturing in mind." },
    ],
    ctaHeading: "Operating under strict compliance requirements?",
    ctaBody: "Let's talk through what governed autonomy looks like for you.",
    ...demoBooking,
  },
  "complex-digital-workflows": {
    title: "Complex Digital Workflows | KlarityIQ",
    category: "Solutions",
    heading: "Complex Digital Workflows",
    lede: "Turn multi-step, cross-system processes into workflows agents can execute end-to-end, with people looped in only where it matters.",
    features: [
      { icon: "⇋", title: "Multi-Step Orchestration", body: "Agents chain decisions across systems, not just single triggers." },
      { icon: "◈", title: "Cross-System Actions", body: "Workflows that span ERP, MES, WMS, and everything in between." },
      { icon: "↝", title: "Exception Handling", body: "Automatically routes edge cases to the right person with full context." },
      { icon: "✎", title: "Built From Your SOPs", body: "Digitize the workflow your team already follows, not a generic template." },
    ],
    ctaHeading: "Have a workflow that spans multiple systems?",
    ctaBody: "Tell us what it looks like today and we'll map the automated version.",
    ...demoBooking,
  },
  "production-management": {
    title: "Production Management | KlarityIQ",
    category: "Solutions",
    heading: "Production Management",
    lede: "Real-time visibility into throughput, quality, and equipment status — with agents that act on what they see.",
    features: [
      { icon: "◈", title: "Live Production Dashboards", body: "Throughput, OEE, and quality in one view." },
      { icon: "✓", title: "Automated Quality Flags", body: "Defects caught and routed in real time." },
      { icon: "◎", title: "Equipment Health", body: "Predictive maintenance built into the same view." },
      { icon: "⇄", title: "Cross-Site Comparison", body: "Benchmark performance across lines and facilities." },
    ],
    ctaHeading: "Want a live view of your own production data?",
    ctaBody: "We'll walk through what a dashboard could look like for your lines.",
    ...demoBooking,
  },
  "inventory-management": {
    title: "Inventory Management | KlarityIQ",
    category: "Solutions",
    heading: "Inventory Management",
    lede: "Real-time stock visibility and agents that flag replenishment needs before a stockout happens.",
    features: [
      { icon: "◈", title: "Real-Time Stock Visibility", body: "Live counts across every location, not end-of-day reconciliation." },
      { icon: "↝", title: "Automated Replenishment Flags", body: "Agents catch low-stock conditions before they become a problem." },
      { icon: "↗", title: "Demand Forecasting", body: "Native ML models predict what you'll need and when." },
      { icon: "⇋", title: "ERP/WMS Sync", body: "Inventory data stays consistent across every connected system." },
    ],
    ctaHeading: "Tired of reconciling inventory after the fact?",
    ctaBody: "Let's talk about what real-time visibility would change.",
    ...demoBooking,
  },
  "traceability-predictability": {
    title: "Traceability & Predictability | KlarityIQ",
    category: "Solutions",
    heading: "Traceability & Predictability",
    lede: "Full genealogy on every batch or unit, plus forecasting models that make outcomes predictable instead of reactive.",
    features: [
      { icon: "⌁", title: "End-to-End Genealogy", body: "Trace every batch or unit from raw material to shipment." },
      { icon: "⌗", title: "Audit-Ready Records", body: "Full digital history record, ready for compliance review." },
      { icon: "↗", title: "Predictive Forecasting", body: "Native ML models forecast outcomes, not just report on the past." },
      { icon: "↝", title: "Exception Alerts", body: "Get flagged the moment something falls outside expected parameters." },
    ],
    ctaHeading: "Need traceability that holds up under audit?",
    ctaBody: "Let's talk about what full genealogy would look like on your line.",
    ...demoBooking,
  },
};
